import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Mountain, Scissors, Users, Anchor, Heart, Target, Star, Compass } from 'lucide-react';
import { AdlerRule } from '../types';

const RULES: AdlerRule[] = [
  { id: 1, title: "Trauma Does Not Exist", concept: "Teleology", description: "Your past does not determine your future. You are not driven by past causes (etiology), but by your present goals (teleology). You choose your lifestyle anew at every moment.", icon: "Compass" },
  { id: 2, title: "All Problems are Interpersonal", concept: "Relationships", description: "To get rid of your problems, all you can do is live in the universe all alone. But you can't. Therefore, all suffering comes from relationships.", icon: "Users" },
  { id: 3, title: "Discard Other People's Tasks", concept: "Separation of Tasks", description: "Do not intervene in other people's tasks, and do not let others intervene in yours. All interpersonal relationship troubles are caused by intruding on other people's tasks.", icon: "Scissors" },
  { id: 4, title: "Do Not Seek Recognition", concept: "Freedom", description: "If you are living in a way to satisfy other people's expectations, you are living other people's lives. Freedom is being disliked by other people.", icon: "Star" },
  { id: 5, title: "Horizontal Relationships", concept: "Equality", description: "Do not praise, and do not rebuke. Treat everyone as equals, not above or below you. Build horizontal relationships based on encouragement.", icon: "Anchor" },
  { id: 6, title: "Community Feeling", concept: "Contribution", description: "Switch from attachment to self (self-interest) to concern for others (social interest). Your worth is found in your contribution to the community.", icon: "Heart" },
  { id: 7, title: "Live in the Here and Now", concept: "The Moment", description: "Life is a series of moments, not a line. Do not look at the past or future. Dance in the here and now. If you shine a bright light on the here and now, you cannot see the past or future.", icon: "Target" },
  { id: 8, title: "The Courage to be Normal", concept: "Self-Acceptance", description: "Why do you want to be special? Because you cannot accept your normal self. Have the courage to be normal. Normal does not mean incapable.", icon: "Mountain" }
];

const IconMap: Record<string, React.ReactNode> = {
  Compass: <Compass size={24} />,
  Users: <Users size={24} />,
  Scissors: <Scissors size={24} />,
  Star: <Star size={24} />,
  Anchor: <Anchor size={24} />,
  Heart: <Heart size={24} />,
  Target: <Target size={24} />,
  Mountain: <Mountain size={24} />,
};

const Courage: React.FC = () => {
  const [openId, setOpenId] = useState<number | null>(1);

  const toggle = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-slate-50 text-slate-600 rounded-full mb-4">
          <Mountain size={32} />
        </div>
        <h1 className="text-4xl font-serif font-bold text-nunchi-900 mb-3">The Courage to Be Disliked</h1>
        <p className="text-xl text-nunchi-500 italic font-serif mb-6">"Freedom is being disliked by other people."</p>
        <p className="text-nunchi-600 max-w-xl mx-auto leading-relaxed">
          Based on Adlerian psychology, this philosophy challenges you to let go of past trauma, 
          stop seeking validation, and find true freedom in the present moment.
        </p>
      </header>

      <div className="space-y-4">
        {RULES.map((rule) => (
          <div 
            key={rule.id} 
            className={`bg-white border rounded-xl overflow-hidden transition-all duration-300 ${openId === rule.id ? 'border-slate-400 shadow-md' : 'border-nunchi-200 hover:border-slate-300'}`}
          >
            <button 
              onClick={() => toggle(rule.id)}
              className="w-full flex items-center justify-between p-5 text-left focus:outline-none"
            >
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-lg ${openId === rule.id ? 'bg-slate-100 text-slate-700' : 'bg-gray-50 text-gray-400'}`}>
                  {IconMap[rule.icon]}
                </div>
                <div>
                  <h3 className={`font-bold text-lg ${openId === rule.id ? 'text-nunchi-900' : 'text-gray-700'}`}>
                    {rule.title}
                  </h3>
                  <p className="text-xs text-nunchi-500 font-serif uppercase tracking-wider">{rule.concept}</p>
                </div>
              </div>
              <div className="text-nunchi-400">
                {openId === rule.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </button>
            
            <div className={`overflow-hidden transition-[max-height] duration-500 ease-in-out ${openId === rule.id ? 'max-h-40' : 'max-h-0'}`}>
              <div className="p-5 pt-0 text-nunchi-600 leading-relaxed border-t border-dashed border-slate-200 mt-2">
                {rule.description}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Courage;